<script setup lang="ts">
// biome-ignore-all lint/style/useTemplate: Init Biome

import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { useCloseOrder } from "@/composables/missions/missions-actions/useCloseOrder";
import { useAuthStore } from "@/modules/auth/store/authStore";

import type { SelectOption } from "@/dsComponents/inputs/types";
// Types
import type { AgencyEvoliaBase } from '@/types/agency';
import type { IMissionDB } from '@/types/mission';

// Ds Components
import Button from "@/dsComponents/buttons/Button.vue";
import Select from "@/dsComponents/inputs/Select.vue";
import Modal from "@/dsComponents/modals/Modal.vue";

interface SendOrderToEvoliaModalProps {
    openAfterCloseOrder?: boolean;
    isOpen: boolean;
    mission: IMissionDB;
}

const props = withDefaults(defineProps<SendOrderToEvoliaModalProps>(), {
    openAfterCloseOrder: false,
});
const { t } = useI18n();
const authStore = useAuthStore();
const toast = useToast();
const evoliaBaseSelectedLabel = ref("");

const contractManagerIdentifierSelected = ref("");
const emits = defineEmits<{
    (e: "close"): void;
    (e: "success", updatedMission: IMissionDB): void;
}>();

const { sendOrderToEvolia, closeMissionWithoutSendToEvolia, evoliaSendInProgress } = useCloseOrder({
    onSuccess: (updatedMission: IMissionDB) => {
        emits("close");
        emits("success", updatedMission);
    },
});

const evoliaBases = computed((): SelectOption[] => {
    const agencyEvoliBases = authStore.currentAgency?.evoliaBases;
    if (!agencyEvoliBases) {
        toast?.open({
            message: t("errors.agency.evoliaBases"),
            type: "error",
            duration: 5000,
        });
        return [];
    }

    // Filter evolia bases to only keep the ones that are linked to the client
    const clientEvoliaIds = props.mission.client?.agenciesList?.map((agency) => agency.agencyEvoliaCode);
    const evoliaBaseFormatted = agencyEvoliBases
        .filter((evoliaBase: AgencyEvoliaBase) => clientEvoliaIds?.includes(String(evoliaBase.evoliaId)))
        .map((evoliaBase: AgencyEvoliaBase) => {
            return {
                label: evoliaBase.evoliaId + " - " + evoliaBase.evoliaBaseName,
                value: evoliaBase.evoliaId.toString(),
            };
        });

    return evoliaBaseFormatted;
});

const handleSelectContractManagerIdentifier = (value: SelectOption) => {
    // biome-ignore lint/style/noNonNullAssertion: Init Biome
    evoliaBaseSelectedLabel.value = value.label!;
    contractManagerIdentifierSelected.value = value.value;
};

const close = () => {
    if (!evoliaSendInProgress.value) {
        emits("close");
    }
    return;
};

onMounted(() => {
    // Use evolia field from currentAgency (replaces contractManagerIdentifier)
    const evoliaId = authStore.currentAgency?.evolia;
    contractManagerIdentifierSelected.value = evoliaId ?? "";
    evoliaBaseSelectedLabel.value = evoliaBases.value.find((e) => e.value === evoliaId)?.label ?? "";
});
</script>

<template>
    <Modal height="auto" width="500px" @close="close" close :open="isOpen">
        <template #header>
            <span class="text-lg semibold">{{ t("modals.SendOrderToEvolia.title.sendToEvolia") }}</span>
        </template>
        <template #default>
            <div class="send-order-to-evolia-modal-content">
                <span v-if="openAfterCloseOrder" class="text-xm regular information">{{ t("modals.SendOrderToEvolia.information") }}</span>
                <Select
                    v-if="evoliaBases.length > 0"
                    :label="t('modals.SendOrderToEvolia.content.selectBase')"
                    :options="evoliaBases"
                    :modelValue="evoliaBaseSelectedLabel"
                    width="100%"
                    @select="handleSelectContractManagerIdentifier"
                />
            </div>
        </template>
        <template #footer>
            <div class="send-order-to-evolia-modal-footer">
                <Button
                    size="md"
                    type="neutral"
                    variant="stroke"
                    v-if="!openAfterCloseOrder"
                    @click="emits('close')"
                    :loading="evoliaSendInProgress"
                    :label="t('global.actions.cancel')"
                />

                <Button
                    size="md"
                    type="neutral"
                    variant="stroke"
                    v-if="openAfterCloseOrder"
                    @click="closeMissionWithoutSendToEvolia(mission.id)"
                    :loading="evoliaSendInProgress"
                    :label="t('modals.SendOrderToEvolia.actions.notSend')"
                />

                <Button
                    size="md"
                    type="primary"
                    variant="filled"
                    class="button-action"
                    @click="sendOrderToEvolia(contractManagerIdentifierSelected ?? '', mission.id)"
                    :loading="evoliaSendInProgress"
                    :label="t('global.actions.send')"
                />
            </div>
        </template>
    </Modal>
</template>

<style lang="scss">
.send-order-to-evolia-modal-content {
    gap: 16px;
    display: flex;
    flex-direction: column;
}

.send-order-to-evolia-modal-footer {
    gap: 24px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
}
</style>
